import { useEffect, useState, useCallback } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge, Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input, Select, Textarea } from "../components/ui/Fields";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError, uploadCpEventImage } from "../api/client";
import type { CpEventListRow, CpEventPayload, CpGift, CpRewardItem, CpGrantRow } from "../api/types";

const tone = (s: string) => (s === "active" ? "success" : s === "scheduled" ? "gold" : s === "ended" ? "neutral" : "warn");
const REWARD_TYPES = [
  ["gold", "Gold (coins)"], ["diamonds", "Diamonds"], ["mall_item", "Mall item"], ["headwear", "Headwear"],
  ["vehicle", "Vehicle"], ["banner", "Banner"], ["other", "Other"],
] as const;
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** datetime-local value <-> ISO. The picker works in the browser's zone; the event timezone only drives the weekly reset. */
const toLocalInput = (iso: string) => { const d = new Date(iso); const p = (n: number) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
const toIso = (v: string) => new Date(v).toISOString();

export function CpEventsPage() {
  const [rows, setRows] = useState<CpEventListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | "new" | null>(null);
  const load = useCallback(() => {
    setLoading(true); setError(null);
    apiRequest<CpEventListRow[]>("/admin/cp-events").then(setRows).catch((e) => setError(e instanceof ApiError ? e.message : "could not load")).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const columns: Column<CpEventListRow>[] = [
    { header: "Event", render: (r) => <div className="flex items-center gap-2">{r.bannerUrl && <img src={r.bannerUrl} alt="" className="h-9 w-16 rounded object-cover" />}<span className="font-medium">{r.title}</span></div> },
    { header: "Status", render: (r) => <span className="flex items-center gap-1"><Badge tone={tone(r.status)}>{r.status}</Badge>{!r.isEnabled && <Badge tone="danger">disabled</Badge>}</span> },
    { header: "Window", render: (r) => <span className="text-xs">{new Date(r.startAt).toLocaleString()} → {new Date(r.endAt).toLocaleString()} ({r.durationDays}d)</span> },
    { header: "Gifts", render: (r) => r.giftCount },
    { header: "Rewards", render: (r) => r.rewardCount },
    { header: "Distributed", render: (r) => r.distributedCycles },
    { header: "", render: (r) => <Button size="sm" variant="secondary" onClick={() => setEditId(r.id)}>Edit</Button> },
  ];
  return (
    <AppShell title="CP Event Management">
      <div className="mb-4 flex items-center justify-between">
        <p className="max-w-xl text-xs text-ink-faint">Weekly CP events: schedule, rules, qualifying gifts and rewards. The Weekly CP website reads this configuration live — nothing is hardcoded.</p>
        <Button onClick={() => setEditId("new")}>Create event</Button>
      </div>
      <DataTable columns={columns} rows={rows} keyField={(r) => r.id} isLoading={loading} error={error} onRetry={load} emptyTitle="No CP events yet" />
      {editId !== null && <EventEditor id={editId} onClose={() => setEditId(null)} onChanged={load} />}
    </AppShell>
  );
}

type Tab = "details" | "gifts" | "rewards" | "distribute";

function EventEditor({ id, onClose, onChanged }: { id: number | "new"; onClose: () => void; onChanged: () => void }) {
  const { showToast } = useToast();
  const [eventId, setEventId] = useState<number | null>(id === "new" ? null : id);
  const [data, setData] = useState<CpEventPayload | null>(null);
  const [tab, setTab] = useState<Tab>("details");

  const reload = useCallback(async (eid: number) => {
    try { setData(await apiRequest<CpEventPayload>(`/admin/cp-events/${eid}`)); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Could not load event", "error"); }
  }, [showToast]);
  useEffect(() => { if (eventId) reload(eventId); }, [eventId, reload]);

  const tabs: Array<[Tab, string]> = [["details", "Details"], ["gifts", "Qualifying gifts"], ["rewards", "Rewards"], ["distribute", "Ranking & distribution"]];
  return (
    <Modal isOpen onClose={onClose} title={eventId ? `Edit event #${eventId}` : "Create event"} footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      <div className="mb-4 flex gap-1 border-b border-surface-border">
        {tabs.map(([k, label]) => (
          <button key={k} disabled={!eventId && k !== "details"} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm ${tab === k ? "border-b-2 border-gold font-semibold text-ink" : "text-ink-muted"} disabled:opacity-40`}>{label}</button>))}
      </div>
      {!eventId && tab === "details" && <p className="mb-3 text-xs text-ink-faint">Save the details first; gifts and rewards are configured right after.</p>}
      {tab === "details" && <DetailsTab data={data} eventId={eventId} onSaved={(p) => { setData(p); setEventId(p.event.id); onChanged(); }} />}
      {tab === "gifts" && data && <GiftsTab data={data} onSaved={(p) => { setData(p); onChanged(); }} />}
      {tab === "rewards" && data && <RewardsTab data={data} onSaved={(p) => { setData(p); onChanged(); }} />}
      {tab === "distribute" && data && <DistributeTab data={data} onChanged={() => { reload(data.event.id); onChanged(); }} />}
    </Modal>
  );
}

function ImageField({ label, url, onChange }: { label: string; url: string; onChange: (u: string) => void }) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  async function pick(f?: File) {
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadCpEventImage(f)); } catch (e) { showToast(e instanceof ApiError ? e.message : "Upload failed", "error"); } finally { setBusy(false); }
  }
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-ink-muted">{label}</label>
      <div className="flex items-center gap-3">
        {url ? <img src={url} alt="" className="h-14 w-24 rounded-lg object-cover" /> : <div className="h-14 w-24 rounded-lg bg-surface-raised" />}
        <div className="flex-1 space-y-1">
          <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(e) => pick(e.target.files?.[0])} className="text-xs text-ink-muted" />
          <Input placeholder="…or paste an image URL" value={url} onChange={(e) => onChange(e.target.value)} />
        </div>
      </div>
    </div>
  );
}

function DetailsTab({ data, eventId, onSaved }: { data: CpEventPayload | null; eventId: number | null; onSaved: (p: CpEventPayload) => void }) {
  const { showToast } = useToast();
  const e = data?.event;
  const [f, setF] = useState({
    title: "", status: "draft", isEnabled: true, startAt: "", endAt: "", durationDays: 14, rulesText: "", timezone: "Asia/Kolkata",
    resetWeekday: 0, resetTime: "00:00", countMode: "both", onCpEnd: "keep", tieBreak: "earliest_reach", bannerUrl: "", backgroundUrl: "",
  });
  useEffect(() => {
    if (!e) {
      const s = new Date(); s.setMinutes(0, 0, 0); const en = new Date(s.getTime() + 14 * 864e5);
      setF((p) => ({ ...p, startAt: toLocalInput(s.toISOString()), endAt: toLocalInput(en.toISOString()) }));
      return;
    }
    setF({ title: e.title, status: e.adminStatus, isEnabled: e.isEnabled, startAt: toLocalInput(e.startAt), endAt: toLocalInput(e.endAt), durationDays: e.durationDays,
      rulesText: e.rulesText, timezone: e.timezone, resetWeekday: e.resetWeekday, resetTime: e.resetTime, countMode: e.countMode, onCpEnd: e.onCpEnd,
      tieBreak: e.tieBreak, bannerUrl: e.bannerUrl ?? "", backgroundUrl: e.backgroundUrl ?? "" });
  }, [e?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  function setStart(v: string) {
    setF((p) => ({ ...p, startAt: v, endAt: v ? toLocalInput(new Date(new Date(v).getTime() + p.durationDays * 864e5).toISOString()) : p.endAt }));
  }
  function setDuration(n: number) {
    setF((p) => ({ ...p, durationDays: n, endAt: p.startAt && n > 0 ? toLocalInput(new Date(new Date(p.startAt).getTime() + n * 864e5).toISOString()) : p.endAt }));
  }
  function setEnd(v: string) {
    setF((p) => ({ ...p, endAt: v, durationDays: p.startAt && v ? Math.max(1, Math.ceil((new Date(v).getTime() - new Date(p.startAt).getTime()) / 864e5)) : p.durationDays }));
  }
  async function save() {
    setBusy(true);
    try {
      const body = { ...f, startAt: toIso(f.startAt), endAt: toIso(f.endAt), resetWeekday: Number(f.resetWeekday) };
      const p = eventId
        ? await apiRequest<CpEventPayload>(`/admin/cp-events/${eventId}`, { method: "POST", body })
        : await apiRequest<CpEventPayload>("/admin/cp-events", { method: "POST", body });
      showToast("Event saved", "success"); onSaved(p);
    } catch (er) { showToast(er instanceof ApiError ? er.message : "Could not save", "error"); } finally { setBusy(false); }
  }
  return (
    <div className="space-y-3">
      <Input label="Event title" value={f.title} onChange={(x) => set("title", x.target.value)} maxLength={80} />
      <div className="grid grid-cols-2 gap-3">
        <Select label="Status" value={f.status} onChange={(x) => set("status", x.target.value)}>
          <option value="draft">Draft (hidden)</option><option value="scheduled">Scheduled</option><option value="active">Active</option><option value="ended">Ended</option></Select>
        <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.isEnabled} onChange={(x) => set("isEnabled", x.target.checked)} /> Enabled (visible on the website)</label>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Input type="datetime-local" label="Start" value={f.startAt} onChange={(x) => setStart(x.target.value)} />
        <Input type="number" min={1} label="Duration (days)" value={f.durationDays} onChange={(x) => setDuration(Number(x.target.value))} />
        <Input type="datetime-local" label="End" value={f.endAt} onChange={(x) => setEnd(x.target.value)} />
      </div>
      <p className="text-xs text-ink-faint">Start/end are entered in your browser's time zone. The event time zone below only decides when each weekly cycle resets.</p>
      <div className="grid grid-cols-3 gap-3">
        <Input label="Event time zone (IANA)" list="cp-tz" value={f.timezone} onChange={(x) => set("timezone", x.target.value)} />
        <datalist id="cp-tz">{["Asia/Kolkata", "UTC", "Asia/Riyadh", "Europe/Istanbul", "Asia/Dubai", "Asia/Manila", "America/New_York"].map((z) => <option key={z} value={z} />)}</datalist>
        <Select label="Weekly reset day" value={f.resetWeekday} onChange={(x) => set("resetWeekday", Number(x.target.value))}>{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</Select>
        <Input type="time" label="Reset time" value={f.resetTime} onChange={(x) => set("resetTime", x.target.value)} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Select label="Count gifts" value={f.countMode} onChange={(x) => set("countMode", x.target.value)}><option value="both">Sent and received</option><option value="sent">Sent only</option><option value="received">Received only</option></Select>
        <Select label="If a CP ends during the event" value={f.onCpEnd} onChange={(x) => set("onCpEnd", x.target.value)}><option value="keep">Keep points earned while together</option><option value="remove">Remove the couple from the ranking</option></Select>
        <Select label="Tie-break" value={f.tieBreak} onChange={(x) => set("tieBreak", x.target.value)}><option value="earliest_reach">Reached the score first</option><option value="earliest_cp">Older CP first</option></Select>
      </div>
      <ImageField label="Event banner" url={f.bannerUrl} onChange={(u) => set("bannerUrl", u)} />
      <ImageField label="Background artwork (optional)" url={f.backgroundUrl} onChange={(u) => set("backgroundUrl", u)} />
      <Textarea label="Rules (one rule per line — the website numbers them)" rows={9} value={f.rulesText} onChange={(x) => set("rulesText", x.target.value)} maxLength={5000} />
      <Button onClick={save} isLoading={busy} disabled={!f.title.trim() || !f.startAt || !f.endAt}>Save details</Button>
    </div>
  );
}

function GiftsTab({ data, onSaved }: { data: CpEventPayload; onSaved: (p: CpEventPayload) => void }) {
  const { showToast } = useToast();
  const [items, setItems] = useState<CpGift[]>(data.gifts);
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Array<{ id: number; name: string; icon_url: string; coins_cost: number }>>([]);
  const [busy, setBusy] = useState(false);
  async function search() {
    try { setFound(await apiRequest("/admin/cp-events/gift-search", { params: { q } })); } catch (e) { showToast(e instanceof ApiError ? e.message : "Search failed", "error"); }
  }
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);
  function add(g: { id: number; name: string; icon_url: string; coins_cost: number }) {
    if (items.some((i) => i.giftId === g.id)) return;
    setItems([...items, { giftId: g.id, name: g.name, iconUrl: g.icon_url, coins: g.coins_cost, price: g.coins_cost, isActive: true }]);
  }
  async function save() {
    setBusy(true);
    try {
      const p = await apiRequest<CpEventPayload>(`/admin/cp-events/${data.event.id}/gifts`, { method: "POST", body: { items: items.map((i) => ({ giftId: i.giftId, coins: i.coins })) } });
      showToast("Qualifying gifts saved", "success"); onSaved(p);
    } catch (e) { showToast(e instanceof ApiError ? e.message : "Could not save", "error"); } finally { setBusy(false); }
  }
  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-faint">Counting mode: <b>{data.event.countMode}</b>. Points = quantity × the coin value below, taken from server-verified gift transactions of users who are in a CP.</p>
      <Card className="divide-y divide-surface-border">
        {items.length === 0 && <p className="p-3 text-sm text-ink-faint">No qualifying gifts yet.</p>}
        {items.map((g) => (
          <div key={g.giftId} className="flex items-center gap-3 p-3">
            <img src={g.iconUrl} alt="" className="h-10 w-10 rounded object-cover" />
            <div className="flex-1"><p className="text-sm font-medium">{g.name}</p><p className="text-xs text-ink-faint">catalogue price {g.price.toLocaleString()}</p></div>
            <div className="w-32"><Input type="number" min={1} label="Coin value" value={g.coins} onChange={(x) => setItems(items.map((i) => (i.giftId === g.giftId ? { ...i, coins: Number(x.target.value) } : i)))} /></div>
            <Button size="sm" variant="danger" onClick={() => setItems(items.filter((i) => i.giftId !== g.giftId))}>Remove</Button>
          </div>))}
      </Card>
      <div className="flex items-end gap-2"><Input label="Find a gift" value={q} onChange={(x) => setQ(x.target.value)} placeholder="name…" /><Button variant="secondary" onClick={search}>Search</Button></div>
      <div className="grid max-h-56 grid-cols-2 gap-2 overflow-auto md:grid-cols-3">
        {found.map((g) => (
          <button key={g.id} onClick={() => add(g)} className="flex items-center gap-2 rounded-lg border border-surface-border p-2 text-left hover:bg-surface-raised">
            <img src={g.icon_url} alt="" className="h-9 w-9 rounded object-cover" /><span className="text-xs"><b>{g.name}</b><br />{g.coins_cost.toLocaleString()}</span></button>))}
      </div>
      <Button onClick={save} isLoading={busy}>Save qualifying gifts</Button>
    </div>
  );
}

function RewardsTab({ data, onSaved }: { data: CpEventPayload; onSaved: (p: CpEventPayload) => void }) {
  const { showToast } = useToast();
  type Row = CpRewardItem & { rank: number };
  const [items, setItems] = useState<Row[]>(data.rewards.flatMap((g) => g.items.map((i) => ({ ...i, rank: g.rank }))));
  const [busy, setBusy] = useState(false);
  const [mallQ, setMallQ] = useState("");
  const [mall, setMall] = useState<Array<{ id: number; name: string; png_asset_url: string | null; duration_days: number | null }>>([]);
  const [pickFor, setPickFor] = useState<number | null>(null);
  const patch = (i: number, p: Partial<Row>) => setItems(items.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  async function searchMall() { try { setMall(await apiRequest("/admin/cp-events/mall-search", { params: { q: mallQ } })); } catch { /* shown empty */ } }
  async function upload(i: number, f?: File) {
    if (!f) return;
    try { patch(i, { imageUrl: await uploadCpEventImage(f, "reward_image") }); } catch (e) { showToast(e instanceof ApiError ? e.message : "Upload failed", "error"); }
  }
  async function save() {
    setBusy(true);
    try {
      const p = await apiRequest<CpEventPayload>(`/admin/cp-events/${data.event.id}/rewards`, { method: "POST", body: { items: items.map((r) => ({ rank: r.rank, type: r.type, name: r.name, imageUrl: r.imageUrl, quantity: r.quantity, durationDays: r.durationDays, mallItemId: r.mallItemId })) } });
      showToast("Rewards saved", "success"); onSaved(p);
    } catch (e) { showToast(e instanceof ApiError ? e.message : "Could not save", "error"); } finally { setBusy(false); }
  }
  const ranks = Array.from(new Set(items.map((r) => r.rank))).sort((a, b) => a - b);
  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-faint">Gold / Diamonds are credited automatically on distribution. Rewards linked to a mall item (Headwear, Vehicle, Mall item, Banner…) are added to the winners' bag automatically; anything else is listed for you to hand out and mark done.</p>
      {items.map((r, i) => (
        <Card key={i} className="space-y-2 p-3">
          <div className="grid grid-cols-4 gap-2">
            <Input type="number" min={1} label="Rank" value={r.rank} onChange={(x) => patch(i, { rank: Number(x.target.value) })} />
            <Select label="Type" value={r.type} onChange={(x) => patch(i, { type: x.target.value })}>{REWARD_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
            <Input label="Name" value={r.name} onChange={(x) => patch(i, { name: x.target.value })} maxLength={60} />
            <Input type="number" min={0} label={r.type === "gold" || r.type === "diamonds" ? "Amount" : "Quantity"} value={r.quantity} onChange={(x) => patch(i, { quantity: Number(x.target.value) })} />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <Input type="number" min={1} label="Duration (days, optional)" value={r.durationDays ?? ""} onChange={(x) => patch(i, { durationDays: x.target.value ? Number(x.target.value) : null })} />
            <div className="col-span-3 flex items-end gap-2">
              <Input label="Image URL" value={r.imageUrl ?? ""} onChange={(x) => patch(i, { imageUrl: x.target.value })} />
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(x) => upload(i, x.target.files?.[0])} className="w-40 text-xs" />
              <Button size="sm" variant="secondary" onClick={() => { setPickFor(i); searchMall(); }}>Existing mall item</Button>
              <Button size="sm" variant="danger" onClick={() => setItems(items.filter((_, idx) => idx !== i))}>Remove</Button>
            </div>
          </div>
          {r.mallItemId && <p className="text-xs text-ink-faint">Linked mall item #{r.mallItemId} (auto-granted)</p>}
        </Card>))}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => setItems([...items, { rank: ranks.length ? ranks[ranks.length - 1] : 1, type: "gold", name: "Gold", imageUrl: null, quantity: 1000, durationDays: null, mallItemId: null }])}>Add reward</Button>
        <Button onClick={save} isLoading={busy}>Save rewards</Button>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold">Preview (what the website shows)</h3>
        {ranks.length === 0 && <p className="text-xs text-ink-faint">No rewards yet.</p>}
        {ranks.map((rk) => (
          <div key={rk} className="mb-3"><p className="mb-1 text-xs font-bold text-pink-600">Top {rk}</p>
            <div className="flex flex-wrap gap-3">{items.filter((r) => r.rank === rk).map((r, i) => (
              <div key={i} className="w-24 text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-xl border-2 border-yellow-400 bg-yellow-50">{r.imageUrl ? <img src={r.imageUrl} alt="" className="max-h-16 max-w-16 object-contain" /> : <span className="text-[10px] text-ink-faint">no image</span>}</div>
                <p className="mt-1 text-xs font-medium">{r.name}</p><p className="text-xs text-pink-600">{r.type === "gold" || r.type === "diamonds" ? r.quantity.toLocaleString() : r.durationDays ? `${r.durationDays} days` : `x${r.quantity}`}</p></div>))}</div></div>))}
      </div>
      {pickFor !== null && (
        <Modal isOpen onClose={() => setPickFor(null)} title="Select an existing mall item" footer={<Button variant="ghost" onClick={() => setPickFor(null)}>Cancel</Button>}>
          <div className="mb-3 flex items-end gap-2"><Input label="Search" value={mallQ} onChange={(x) => setMallQ(x.target.value)} /><Button variant="secondary" onClick={searchMall}>Search</Button></div>
          <div className="grid max-h-72 grid-cols-2 gap-2 overflow-auto">
            {mall.map((m) => (
              <button key={m.id} className="flex items-center gap-2 rounded-lg border border-surface-border p-2 text-left hover:bg-surface-raised"
                onClick={() => { patch(pickFor, { mallItemId: m.id, name: items[pickFor].name && items[pickFor].name !== "Gold" ? items[pickFor].name : m.name, imageUrl: m.png_asset_url, durationDays: items[pickFor].durationDays ?? m.duration_days, type: items[pickFor].type === "gold" || items[pickFor].type === "diamonds" ? "mall_item" : items[pickFor].type }); setPickFor(null); }}>
                {m.png_asset_url && <img src={m.png_asset_url} alt="" className="h-10 w-10 object-contain" />}<span className="text-xs">{m.name}</span></button>))}
          </div>
        </Modal>)}
    </div>
  );
}

function DistributeTab({ data, onChanged }: { data: CpEventPayload; onChanged: () => void }) {
  const { showToast } = useToast();
  const [grants, setGrants] = useState<CpGrantRow[]>([]);
  const [cycle, setCycle] = useState(1);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadGrants = useCallback(() => { apiRequest<CpGrantRow[]>(`/admin/cp-events/${data.event.id}/grants`).then(setGrants).catch(() => setGrants([])); }, [data.event.id]);
  useEffect(loadGrants, [loadGrants]);
  async function distribute() {
    setBusy(true);
    try {
      const r = await apiRequest<{ granted: number; manual: number }>(`/admin/cp-events/${data.event.id}/distribute`, { method: "POST", body: { cycle } });
      showToast(`Distributed: ${r.granted} granted, ${r.manual} to hand out manually`, "success"); setConfirm(false); loadGrants(); onChanged();
    } catch (e) { showToast(e instanceof ApiError ? e.message : "Could not distribute", "error"); setConfirm(false); } finally { setBusy(false); }
  }
  async function fulfil(id: number) {
    try { await apiRequest(`/admin/cp-events/grants/${id}/fulfil`, { method: "POST", body: {} }); loadGrants(); } catch (e) { showToast(e instanceof ApiError ? e.message : "Failed", "error"); }
  }
  return (
    <div className="space-y-4">
      <p className="text-sm">Current status: <Badge tone={tone(data.event.status)}>{data.event.status}</Badge>
        {data.cycle && <span className="ml-2 text-xs text-ink-faint">current cycle #{data.cycle.no} ends {new Date(data.cycle.to).toLocaleString()}</span>}</p>
      <h3 className="text-sm font-semibold">This week's ranking (live from the server)</h3>
      <Card className="divide-y divide-surface-border">
        {data.ranking.length === 0 && <p className="p-3 text-sm text-ink-faint">No qualifying points yet.</p>}
        {data.ranking.map((r) => (<div key={r.coupleId} className="flex items-center gap-3 p-2 text-sm"><b className="w-6">{r.rank}</b>
          <span className="flex-1">{r.a.username} &amp; {r.b.username}</span><span>{r.points.toLocaleString()}</span></div>))}
      </Card>
      <div className="flex items-end gap-2">
        <div className="w-32"><Input type="number" min={1} label="Cycle #" value={cycle} onChange={(x) => setCycle(Number(x.target.value))} /></div>
        <Button variant="danger" onClick={() => setConfirm(true)} disabled={cycle < 1}>Distribute rewards for this cycle</Button>
      </div>
      <p className="text-xs text-ink-faint">Only a finished cycle can be distributed, once. The final ranking is frozen and rewards go to both partners of each rewarded couple. After the first distribution the schedule, counting rules and qualifying gifts are locked.</p>
      <h3 className="text-sm font-semibold">Distributed rewards</h3>
      <Card className="divide-y divide-surface-border">
        {grants.length === 0 && <p className="p-3 text-sm text-ink-faint">Nothing distributed yet.</p>}
        {grants.map((g) => (<div key={g.id} className="flex items-center gap-3 p-2 text-xs"><span>C{g.cycle} · Top {g.rank}</span><span className="flex-1">{g.user.username} #{g.user.publicId} — {g.rewardName}{g.type === "gold" || g.type === "diamonds" ? ` ×${g.quantity}` : ""}</span>
          <Badge tone={g.status === "granted" ? "success" : g.status === "manual" ? "warn" : "danger"}>{g.status}</Badge>
          {g.status === "manual" && <Button size="sm" variant="secondary" onClick={() => fulfil(g.id)}>Mark handed out</Button>}</div>))}
      </Card>
      <ConfirmDialog isOpen={confirm} onClose={() => setConfirm(false)} onConfirm={distribute} isLoading={busy} isDangerous title={`Distribute cycle ${cycle}?`}
        description="This freezes the cycle's ranking and credits the rewards. It cannot be repeated or undone from here." confirmLabel="Distribute" />
    </div>
  );
}
